/**
 * SandboxExpiryCountdown — live "expires in 1h 24m" ticker for the active
 * sandbox instance. Updates once per second. Color shifts based on urgency:
 * muted >2h, amber ≤2h, red ≤30m. Two render sizes ("compact" for the header
 * context strip, "large" for the Lifecycle card hero).
 */
import React from 'react';
import { cn } from '@chatsdet/ui';
import { useTickingNow } from '../hooks/useTickingNow';

export interface SandboxExpiryCountdownProps {
  expiresAt: string | Date;
  /** "compact" inline strip vs "large" bold hero. */
  variant?: 'compact' | 'large';
  /** Optional label prefix in compact mode (default: "Expires in"). */
  prefix?: string | null;
  className?: string;
}

function formatDelta(ms: number): string {
  if (ms <= 0) return 'expired';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
  return `${seconds}s`;
}

export function SandboxExpiryCountdown({
  expiresAt,
  variant = 'compact',
  prefix = 'Expires in',
  className,
}: SandboxExpiryCountdownProps) {
  const now = useTickingNow();
  const target = typeof expiresAt === 'string' ? new Date(expiresAt).getTime() : expiresAt.getTime();
  const remaining = target - now;
  const formatted = formatDelta(remaining);

  // Urgency color: muted >2h, amber ≤2h, red ≤30m
  const urgencyClass =
    remaining <= 30 * 60 * 1000
      ? 'text-red-600'
      : remaining <= 2 * 60 * 60 * 1000
      ? 'text-amber-600'
      : 'text-muted-foreground';

  if (variant === 'large') {
    return (
      <span
        className={cn(
          'text-3xl font-bold tracking-tight tabular-nums',
          urgencyClass,
          className
        )}
      >
        {formatted}
      </span>
    );
  }

  return (
    <span className={cn('inline-flex items-center gap-1 tabular-nums', urgencyClass, className)}>
      {prefix ? <span>{prefix}</span> : null}
      <span className="font-medium">{formatted}</span>
    </span>
  );
}

SandboxExpiryCountdown.displayName = 'SandboxExpiryCountdown';
